import React from 'react';
import { t } from '@lingui/macro';
import { FormsUpdateQuestionMutationInput } from './__generated__/EditQuestionCard_updateQuestion_Mutation.graphql';
import { Input } from 'components/Form/Input';
import { DEFAULT_QUESTION_TITLE } from '../CreateQuestionButton';
import { graphql, useFragment } from 'utils/graphClient';
import { QuestionTitle_question$key } from './__generated__/QuestionTitle_question.graphql';
import { FormQuestionType } from 'components/icons/QuestionIcon';

const fragment = graphql`
  fragment QuestionTitle_question on FormQuestion {
    id
    text
    definition {
      __typename
      ... on CustomField__String {
        id
      }
      ... on CustomField__Numeric {
        minValue
        maxValue
      }
      ... on CustomField__Checkboxes {
        id
        options {
          value
          text
        }
      }
      ... on CustomField__MultipleChoice {
        id
        options {
          value
          text
        }
      }
      ... on CustomField__Date {
        id
      }
      ... on CustomField__Time {
        id
      }
      ... on CustomField__DateTime {
        id
      }
      ... on CustomField__PhoneNumber {
        id
      }
      ... on CustomField__Email {
        id
      }
      ... on CustomField__Link {
        id
      }
    }
  }
`;

interface QuestionTitleProps {
  question: QuestionTitle_question$key;
  isDisabled: boolean;
  saveQuestion: (input: FormsUpdateQuestionMutationInput) => void;
}

export const QuestionTitle = ({
  question,
  isDisabled,
  saveQuestion,
}: QuestionTitleProps) => {
  const { id, text, definition } = useFragment(fragment, question);

  const handleInputChange = () => {
    switch (definition?.__typename) {
      case FormQuestionType.TEXT:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          stringDefinition: {},
        });
        break;
      case FormQuestionType.NUMERIC:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          numericDefinition: {
            minValue: definition.minValue,
            maxValue: definition.maxValue,
          },
        });
        break;
      case FormQuestionType.CHECKBOXES:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          checkboxesDefinition: {
            options: definition.options,
          },
        });
        break;
      case FormQuestionType.MULTIPLECHOICE:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          multipleChoiceDefinition: {
            options: definition.options,
          },
        });
        break;
      case FormQuestionType.DATE:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          dateDefinition: {},
        });
        break;
      case FormQuestionType.TIME:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          timeDefinition: {},
        });
        break;
      case FormQuestionType.DATETIME:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          dateTimeDefinition: {},
        });
        break;
      case FormQuestionType.PHONE:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          phoneNumberDefinition: {},
        });
        break;
      case FormQuestionType.EMAIL:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          emailDefinition: {},
        });
        break;
      case FormQuestionType.LINK:
        saveQuestion({
          id,
          text: inputRef.current?.value || undefined,
          linkDefinition: {},
        });
        break;
      default:
        break;
    }
  };

  const inputRef = React.createRef<HTMLInputElement>();

  return (
    <Input
      mb={3}
      type="text"
      ref={inputRef}
      isDisabled={isDisabled}
      placeholder={t`Ask a question...`}
      defaultValue={text === DEFAULT_QUESTION_TITLE() ? '' : text}
      onChange={handleInputChange}
    />
  );
};
